import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import authService from '../../services/authService';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCurrentUser, setToken } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');
      const type = searchParams.get('type');

      if (error) {
        const errorMessage = error === 'google_auth_failed' 
          ? 'Google authentication failed. Please try again.' 
          : 'Authentication failed. Please try again.';
        toast.error(errorMessage);
        navigate('/login');
        return;
      }

      if (token) {
        try {
          // Store token in localStorage
          localStorage.setItem('token', token);
          
          // Get user data
          const userData = await authService.getCurrentUser();
          
          // Update auth context
          setCurrentUser(userData);
          setToken(token);
          
          // Show success message
          const authType = type === 'google' ? 'Google' : '';
          toast.success(`Successfully signed in${authType ? ' with ' + authType : ''}!`);
          
          // Check for stored redirect URL (from invitation flows)
          const pendingRedirect = localStorage.getItem('authRedirectUrl');
          if (pendingRedirect) {
            localStorage.removeItem('authRedirectUrl');
            navigate(pendingRedirect);
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error setting up authentication:', error);
          toast.error('Authentication setup failed. Please try logging in again.');
          localStorage.removeItem('token');
          navigate('/login');
        }
      } else {
        // No token or error, redirect to login
        navigate('/login');
      }
    };

    handleCallback();
  }, [searchParams, navigate, setCurrentUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-blue-600">
            <svg className="animate-spin h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Setting up your account...
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we complete your authentication.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
